import {IPresenter} from "@/rawdogjs/src/interfaces/IPresenter";
import {ErrorBase} from "@/rawdogjs/src/core/ErrorBase";
import {ContactStepView} from "./contactStepView";
import {TenantApi} from "../../../services/api/base/TenantApi";
import {UserStore} from "../../../data/stores/UserStore";
import {Router} from "@/rawdogjs/src/router";
import {BookingApi} from "../../../services/api/base/BookingApi";
import {BookingStore} from "../../../data/stores/BookingStore";
import {TenantProfileStore} from "../../../data/stores/TenantProfileStore";
import {LoadingPresenter} from "../../loading/loadingPresenter";
import {ParcelDependencyContainer} from "@/rawdogjs/src/core/ParcelDependencyContainer";
import {RenderComponent} from "@/rawdogjs/src/renderRoute";
import {ValidationError} from "@/rawdogjs/src/core/ValidationError";
import {UuidHelper} from "../../../shared/IdentityHelper";

export class ContactStepPresenter implements IPresenter {

    public errors: ErrorBase[] = [];
    public serverErrors: ErrorBase[] = [];

    public readonly _view: ContactStepView;

    private readonly _tenantApi: TenantApi;
    private readonly _userStore: UserStore;
    private readonly _router: Router;
    private readonly _bookingApi: BookingApi;
    private _bookingStore: BookingStore;
    private _tenantProfileStore: TenantProfileStore;
    private _loading: LoadingPresenter;

    constructor(view: ContactStepView, router: Router, dependencyInjection: ParcelDependencyContainer) {

        this._view = view;
        this._router = router;

        this._tenantApi = dependencyInjection.resolve<TenantApi>('TenantApi');
        this._bookingApi = dependencyInjection.resolve<BookingApi>('BookingApi');
        this._userStore = dependencyInjection.resolve<UserStore>('UserStore');
        this._tenantProfileStore = dependencyInjection.resolve<TenantProfileStore>('TenantProfileStore');
        this._bookingStore = dependencyInjection.resolve<BookingStore>('BookingStore');

    }

    async handleFormChange(fieldSelector: string, value: string, fieldName: string) {
        // validate and then set the field errors
        await this.validate();
        this._view.setFieldErrors(this.getCombinedErrors(), fieldName);
    }

    async initialize() {

        const renderComponent = new RenderComponent(this._router);
        this._loading = await renderComponent.render<LoadingPresenter>('loading');
        this._view.$element.append(this._loading._view.$element);

        // start up with the sign-up button being disabled
        this._view.isSignUpButtonEnable = false;

        this._view.bind(
            this,
            async (fieldSelector, value, fieldName) => {
                await this.handleFormChange(fieldSelector, value, fieldName);
            }
        );

        this._loading.setBody('Loading...');
        this._loading.show(true);

        await this._bookingStore.pull();
        
        this._loading.show(false);


        let booking = this._bookingStore.getBooking();

        if (booking) {
            this._view.phoneNumber = booking.phoneNumber;
            await this.validate();
        }

        this._view.scrollToTop();

        // set focus on email field on start up
        setTimeout(() => {
            this._view.focusOnElement('phone-number');
        }, 0);

    }

    async validate() {

        this.errors = [];

        if (!this.isValidUSPhoneNumber(this._view.phoneNumber)) {
            this.errors.push(
                new ValidationError(
                    UuidHelper.newId(),
                    'Incomplete or invalid Phone Number.',
                    'contactStepPresenter.InvalidPhoneNumber',
                    '5a3a79c5-d041-405a-8cee-a8dbdedda0ac',
                    'phoneNumber'
                )
            );
        }

        this._view.isSignUpButtonEnable = (this.getCombinedErrors().length === 0);
    }

    isValidUSPhoneNumber(rawInput: string): boolean {
        // Remove all non-numeric characters
        let digits = rawInput.replace(/\D/g, '');

        // If starts with '1' and length is 11, strip the leading '1'
        if (digits.length === 11 && digits.startsWith('1')) {
            digits = digits.substring(1);
        }

        // Check if exactly 10 digits remain
        return digits.length === 10;
    }

    getCombinedErrors() {
        return this.errors.concat(this.serverErrors);
    }

    async submit() {

        try {

            const booking = this._bookingStore.getBooking();

            this._loading.show(true);
            const setPhoneNumberResult = await this._bookingApi.post({
                    "setPhoneNumber": {
                        tenantId: this._userStore.getTenantId(),
                        bookingId: booking.id,
                        phoneNumber: this._view.phoneNumber
                    }
                },
                this._userStore.getParentTenant(),
                true,
                false
            );

            if (setPhoneNumberResult?.status === 200) {
                await this.refreshModels();
                this._router.navigate('/#/drywallio/booking-confirmation');
            }

        } catch (e) {

            if (e.status === 400) {
                if (e.response?.data?.errors && e.response?.data?.errors.length > 0) {
                    this._view.setErrorList(e.response?.data?.errors);
                }
            }

        }
        finally{
            this._loading.show(false);
        }
    }

    private async refreshModels() {

        this._loading.setBody('Loading data...');
        this._loading.show(true);

        await this._userStore.fetch(this._tenantApi);

        await this._bookingStore.fetch(
            this._bookingApi,
            this._tenantProfileStore.getTenantProfile().currentWorkingBookingId,
            this._userStore.getParentTenant()
        )

        this._loading.show(false);

    }

    inject() {
    }

}
